import { keycloakConfig, keycloakEndpoints } from "~/config/keycloak";
import {
  type KeycloakTokenResponse,
  type KeycloakTokens,
  type KeycloakUserInfo,
  type UserProfile,
} from "~/features/auth/types/keycloak";
import { decodeJWT, extractPartnerId, extractRolesFromToken } from "~/features/auth/utils/jwt";
import {
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
} from "~/features/auth/utils/pkce";
import {
  clearOAuthState,
  clearTokens,
  consumeForceLoginPrompt,
  getAndClearPreAuthUrl,
  getOAuthState,
  getStoredTokens,
  setForceLoginPrompt,
  storeOAuthState,
  storePreAuthUrl,
  storeTokens,
} from "~/features/auth/utils/tokenStorage";

import { assertTokenResponse, assertUserInfoResponse, parseOrganizations } from "./keycloakClaims";

export type { UserProfile };
export { getStoredTokens };

const TOKEN_REFRESH_BUFFER_MS = 120_000;
const KEYCLOAK_REQUEST_TIMEOUT_MS = 15_000;

/**
 * Every raw `fetch` in this file used to have no timeout at all — unlike
 * `apiClient`'s `executeRequest`, which bounds every request with an
 * `AbortController`. A stalled Keycloak token-endpoint request left
 * `refreshTokens` (and the `refreshPromise` single-flight guard wrapping it in
 * `ensureValidTokens`) pending forever: observed live as a workflow-run status
 * poll that hit a 401, tried `getAccessToken()` to refresh, and never got
 * control back — the mutation just stopped responding, no toast, no redirect.
 * Keycloak's token endpoint normally answers in well under a second, so 15s is
 * generous rather than tight.
 */
const fetchWithTimeout = (
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs: number = KEYCLOAK_REQUEST_TIMEOUT_MS,
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(input, { ...init, signal: controller.signal }).finally(() => {
    clearTimeout(timeoutId);
  });
};

let refreshPromise: Promise<KeycloakTokens> | null = null;

const exchangeCodeForTokens = async (
  code: string,
  codeVerifier: string,
): Promise<KeycloakTokenResponse> => {
  const response = await fetchWithTimeout(keycloakEndpoints.token, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: keycloakConfig.clientId,
      code,
      code_verifier: codeVerifier,
      redirect_uri: keycloakConfig.redirectUri,
    }),
  });

  if (!response.ok) {
    throw new Error(`Token exchange failed: ${response.status}`);
  }

  const data: unknown = await response.json();

  assertTokenResponse(data);

  return data;
};

const claimsToUserInfo = (claims: Record<string, unknown> | null): KeycloakUserInfo | null => {
  if (!claims || typeof claims.sub !== "string" || typeof claims.email !== "string") {
    return null;
  }

  return {
    sub: claims.sub,
    name: (claims.name as string) ?? "",
    given_name: (claims.given_name as string) ?? "",
    family_name: (claims.family_name as string) ?? "",
    email: claims.email,
    preferred_username: (claims.preferred_username as string) ?? "",
    email_verified: (claims.email_verified as boolean) ?? false,
    organization: claims.organization as KeycloakUserInfo["organization"],
  };
};

const fetchUserInfoFromEndpoint = async (accessToken: string): Promise<KeycloakUserInfo> => {
  const response = await fetchWithTimeout(keycloakEndpoints.userInfo, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!response.ok) {
    throw new Error(`Userinfo request failed: ${response.status}`);
  }

  const data: unknown = await response.json();
  assertUserInfoResponse(data);

  return data;
};

/**
 * Resolves OIDC user profile claims. Prefers the access-token JWT (full tokens),
 * then the id_token (lightweight access tokens), then the `/userinfo` endpoint.
 *
 * Lightweight access tokens omit `sub`/`email`/org claims to keep SNS-bound API
 * requests small; userinfo uses the same small bearer and is proxied in dev.
 */
export const resolveUserInfo = async (
  accessToken: string,
  idToken?: string,
): Promise<KeycloakUserInfo> => {
  const fromAccess = claimsToUserInfo(decodeJWT(accessToken));
  if (fromAccess) {
    return fromAccess;
  }

  if (idToken) {
    const fromId = claimsToUserInfo(decodeJWT(idToken));
    if (fromId) {
      return fromId;
    }
  }

  return fetchUserInfoFromEndpoint(accessToken);
};

/**
 * Resolves ABAC permission roles from the JWT, falling back to OIDC userinfo when
 * the access token is lightweight (roles omitted to keep SNS-bound requests small).
 */
const resolvePermissionRolesFromClaims = (
  accessToken: string,
  idToken?: string,
  userInfo?: KeycloakUserInfo,
): string[] => {
  const fromAccess = extractRolesFromToken(decodeJWT(accessToken));
  if (fromAccess.length > 0) {
    return fromAccess;
  }

  if (idToken) {
    const fromId = extractRolesFromToken(decodeJWT(idToken));
    if (fromId.length > 0) {
      return fromId;
    }
  }

  if (userInfo) {
    return extractRolesFromToken(userInfo as unknown as Record<string, unknown>);
  }

  return [];
};

export const resolvePermissionRoles = async (
  accessToken: string,
  idToken?: string,
  userInfo?: KeycloakUserInfo,
): Promise<string[]> => {
  const fromClaims = resolvePermissionRolesFromClaims(accessToken, idToken, userInfo);
  if (fromClaims.length > 0) {
    return fromClaims;
  }

  try {
    const response = await fetchWithTimeout(keycloakEndpoints.userInfo, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) {
      return [];
    }

    const userinfo = (await response.json()) as Record<string, unknown>;
    const fromUserinfo = extractRolesFromToken(userinfo);

    return fromUserinfo;
  } catch {
    return [];
  }
};

const buildUserProfile = (
  userInfo: KeycloakUserInfo,
  accessToken: string,
  roles: string[],
  idToken?: string,
): UserProfile => {
  const accessClaims = decodeJWT(accessToken);
  const idClaims = idToken ? decodeJWT(idToken) : null;
  const partnerId = extractPartnerId(accessClaims) ?? extractPartnerId(idClaims);
  const organizations = parseOrganizations(
    accessClaims?.organization ?? idClaims?.organization ?? userInfo.organization,
  );
  const firstOrg = organizations[0];

  if (!firstOrg?.tenantId) {
    throw new Error(
      "No organization found in token claims. The user account may not be configured correctly in Keycloak.",
    );
  }

  return {
    id: userInfo.sub,
    name: userInfo.name,
    email: userInfo.email,
    username: userInfo.preferred_username,
    tenantId: firstOrg.tenantId,
    organization: firstOrg.name,
    country: firstOrg.country,
    organizations,
    roles,
    partnerId,
  };
};

const refreshTokens = async (refreshToken: string): Promise<KeycloakTokens> => {
  const response = await fetchWithTimeout(keycloakEndpoints.token, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      client_id: keycloakConfig.clientId,
      refresh_token: refreshToken,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to refresh token");
  }

  const data: unknown = await response.json();

  assertTokenResponse(data);

  return storeTokens(data);
};

const startLogin = async (returnTo?: string): Promise<void> => {
  const currentUrl = window.location.href;
  let preAuthUrl: string;
  if (returnTo) {
    preAuthUrl = `${window.location.origin}${returnTo}`;
  } else if (currentUrl.includes("/login")) {
    preAuthUrl = window.location.origin;
  } else {
    preAuthUrl = currentUrl;
  }
  storePreAuthUrl(preAuthUrl);

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();
  storeOAuthState(state, codeVerifier);

  const params = new URLSearchParams({
    client_id: keycloakConfig.clientId,
    response_type: "code",
    scope: "openid profile email",
    redirect_uri: keycloakConfig.redirectUri,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });

  // After an explicit logout the SSO session cookie may survive (e.g. the
  // logout id_token_hint was stale), which would silently re-authenticate the
  // same user. prompt=login forces Keycloak to show the credential form so a
  // different account can sign in. Normal/session-expiry logins keep silent SSO.
  if (consumeForceLoginPrompt()) {
    params.set("prompt", "login");
  }

  window.location.href = `${keycloakEndpoints.authorization}?${params.toString()}`;
};

/**
 * Single-flight guard around {@link startLogin}.
 *
 * A login redirect rewrites the stored PKCE pair and then leaves the page, so it
 * must happen exactly once. Two callers can race today — `apiClient` redirects on
 * a 401 while the auth hook's own `loginWithKeycloak` fires — and each would
 * generate a fresh `state`/`code_verifier`. The pair left in storage belongs to
 * whichever wrote last, while the browser navigates carrying the other one's
 * `state`, so the callback fails its state check and the user sees
 * "Authentication Failed". Retrying works because that attempt runs alone.
 *
 * It also protects the one-shot `prompt=login` flag: a duplicate call would
 * consume it, silently dropping the credential prompt for an account switch.
 *
 * Never reset on success — success means the page is navigating away, and this
 * module dies with it.
 */
let loginInFlight: Promise<void> | null = null;

export const initiateLogin = (returnTo?: string): Promise<void> => {
  loginInFlight ??= startLogin(returnTo).catch((error: unknown) => {
    // No redirect happened, so a later attempt must be allowed to try again.
    loginInFlight = null;

    throw error;
  });

  return loginInFlight;
};

export const handleCallback = async (
  code: string,
  state: string,
): Promise<{ userProfile: UserProfile; tokens: KeycloakTokens; redirectTo: string }> => {
  const { state: storedState, codeVerifier } = getOAuthState();

  if (!storedState || storedState !== state) {
    throw new Error("Invalid state parameter");
  }

  if (!codeVerifier) {
    throw new Error("Missing code verifier");
  }

  clearOAuthState();

  const tokenResponse = await exchangeCodeForTokens(code, codeVerifier);
  const userInfo = await resolveUserInfo(tokenResponse.access_token, tokenResponse.id_token);
  // Always resolve roles after profile claims — lightweight access tokens omit ABAC roles.
  const roles = await resolvePermissionRoles(
    tokenResponse.access_token,
    tokenResponse.id_token,
    userInfo,
  );
  const userProfile = buildUserProfile(
    userInfo,
    tokenResponse.access_token,
    roles,
    tokenResponse.id_token,
  );

  const tokens = storeTokens(tokenResponse);

  // Resolve the post-login destination. The caller is responsible for
  // performing the navigation via React Router (no full-page reload).
  let redirectTo = "/";
  const preAuthUrl = getAndClearPreAuthUrl();

  if (preAuthUrl) {
    try {
      const target = new URL(preAuthUrl);

      if (
        target.origin === window.location.origin &&
        !target.pathname.startsWith("/auth/callback") &&
        !target.pathname.startsWith("/login")
      ) {
        redirectTo = target.pathname + target.search + target.hash;
      }
    } catch {
      // Malformed URL — fall through to default "/"
    }
  }

  return { userProfile, tokens, redirectTo };
};

export const ensureValidTokens = async (): Promise<KeycloakTokens | null> => {
  const tokens = getStoredTokens();

  if (!tokens) {
    return null;
  }

  if (Date.now() < tokens.expires_at - TOKEN_REFRESH_BUFFER_MS) {
    return tokens;
  }

  if (!tokens.refresh_token) {
    clearTokens();
    return null;
  }

  if (refreshPromise) {
    try {
      return await refreshPromise;
    } catch {
      return null;
    }
  }

  refreshPromise = refreshTokens(tokens.refresh_token).finally(() => {
    refreshPromise = null;
  });

  try {
    return await refreshPromise;
  } catch {
    clearTokens();
    return null;
  }
};

export const logout = (): void => {
  // Save current path so the user returns here after re-login
  const currentPath = window.location.pathname + window.location.search;
  if (currentPath !== "/login" && currentPath !== "/") {
    storePreAuthUrl(window.location.origin + currentPath);
  }

  const tokens = getStoredTokens();
  const idToken = tokens?.id_token;

  clearTokens();

  // Logging out is an explicit "switch account" signal: force the next login to
  // prompt for credentials even if Keycloak's SSO session cookie outlives this
  // end-session call. Set after clearTokens (which doesn't touch this flag).
  setForceLoginPrompt();

  const logoutUrl = new URL(keycloakEndpoints.logout);

  if (idToken) {
    logoutUrl.searchParams.set("id_token_hint", idToken);
  }

  // Keycloak 18+ requires client_id when validating post_logout_redirect_uri.
  logoutUrl.searchParams.set("client_id", keycloakConfig.clientId);
  logoutUrl.searchParams.set("post_logout_redirect_uri", keycloakConfig.logoutRedirectUri);

  window.location.replace(logoutUrl.toString());
};

export const getUserProfile = async (): Promise<UserProfile> => {
  const tokens = getStoredTokens();

  if (!tokens) {
    throw new Error("No tokens available");
  }

  const userInfo = await resolveUserInfo(tokens.access_token, tokens.id_token);
  const roles = await resolvePermissionRoles(tokens.access_token, tokens.id_token, userInfo);

  return buildUserProfile(userInfo, tokens.access_token, roles, tokens.id_token);
};

export const getAccessToken = async (): Promise<string | null> => {
  const tokens = await ensureValidTokens();

  return tokens?.access_token || null;
};

/**
 * Force-refresh the access token regardless of expiry.
 * Use after Keycloak role/group changes, so the new JWT includes updated claims.
 */
export const forceRefreshTokens = async (): Promise<KeycloakTokens | null> => {
  const tokens = getStoredTokens();

  if (!tokens?.refresh_token) {
    return null;
  }

  try {
    return await refreshTokens(tokens.refresh_token);
  } catch {
    return null;
  }
};

export const isAuthenticated = (): boolean => !!getStoredTokens()?.refresh_token;

export const clearSession = clearTokens;
